import apiClient from "../../client/apiClient"
import { appendQueryParams } from "../../utils"

export type Session = {
  _id: string
  title: string
  startDate: string
  endDate: string
  isActive: boolean
  status?: string
  isDeleted?: boolean
  deletedAt?: string
  deletedBy?: string
  createdAt: string
  updatedAt: string
  openForRegistration?: boolean
  // Only populated when fetched with `all: true` (the branch-scoped default
  // fetch never needs it, since it's implicitly "your own campus").
  branch?: { _id: string; name: string } | string | null
}

export type PaginationData = {
  currentPage: number
  hasNextPage: boolean
  hasPrevPage: boolean
  limit: number
  total: number
  totalPages: number
}

export type CreateSessionData = {
  title: string
  startDate: string
  endDate: string
  isActive?: boolean
  status?: string
}

export type UpdateSessionData = {
  title?: string
  startDate?: string
  endDate?: string
  status?: string
  openForRegistration?: boolean
}

export type SessionsResponse = {
  sessions: Session[]
  pagination: PaginationData
}

const SESSION_ENDPOINT = {
  SESSIONS: "/session",
}

const getAllSessions = async ({
  page = 1,
  limit = 10,
  sortBy,
  status,
  title,
  searchText: search,
  all,
}: {
  page?: number
  limit?: number
  sortBy?: string
  status?: string
  title?: string
  searchText?: string,
  all?: boolean
} = {}): Promise<SessionsResponse> => {
  const response = await apiClient({
    url: appendQueryParams(SESSION_ENDPOINT.SESSIONS, {
      page,
      limit,
      sortBy,
      status,
      title,
      searchText: search,
      // Super-admin only: list EVERY campus's sessions (for the copy-fees picker).
      all: all ? "true" : undefined,
    }),
    method: "GET",
  })
  return response.data
}

const getSessionById = async (id: string): Promise<Session> => {
  const response = await apiClient({
    url: `${SESSION_ENDPOINT.SESSIONS}/${id}`,
    method: "GET",
  })
  return response.data
}

const createSession = async (data: CreateSessionData): Promise<Session> => {
  const response = await apiClient({
    url: `${SESSION_ENDPOINT.SESSIONS}/create`,
    method: "POST",
    data,
  })
  return response.data
}

const updateSession = async (id: string, data: UpdateSessionData): Promise<Session> => {
  const response = await apiClient({
    url: `${SESSION_ENDPOINT.SESSIONS}/${id}`,
    method: "PUT",
    data,
  })
  return response.data
}

// A class with its own date override WITHIN this session -- e.g. the
// session is "Aug 2026-2027" but Matric Class IX/X actually run Apr-Mar.
// Deliberately not a second session: everything that already points at
// THIS session (enrollments, head count, invoices, attendance) is
// untouched -- only the handful of places that need a specific class's
// real calendar (exam dates, fee billing windows) read this.
export type SessionScope = {
  _id: string
  session: string
  classes: { _id: string; name: string }[]
  startDate: string
  endDate: string
}

export type SessionScopeInput = {
  classes: string[]
  startDate: string
  endDate: string
}

const getSessionScopes = async (sessionId: string): Promise<SessionScope[]> => {
  const response = await apiClient({
    url: `${SESSION_ENDPOINT.SESSIONS}/${sessionId}/scopes`,
    method: "GET",
  })
  return response.data.data
}

// Full replace -- sends the whole set of overrides for this session in one
// call, same pattern as Term's scopes.
const setSessionScopes = async (
  sessionId: string,
  scopes: SessionScopeInput[]
): Promise<SessionScope[]> => {
  const response = await apiClient({
    url: `${SESSION_ENDPOINT.SESSIONS}/${sessionId}/scopes`,
    method: "PUT",
    data: { scopes },
  })
  return response.data.data
}

export {
  getAllSessions,
  getSessionById,
  createSession,
  updateSession,
  getSessionScopes,
  setSessionScopes,
}